import { memo } from "react";

import {
    MonthTimeRange,
    ReportOverviewData,
    ReportSummaryInfo,
    ReportTimeRangeType,
} from "types/Reports";

import { REPORT_SENSOR_REGISTRY } from "../../constant/report";
import { transformDataToOUCParams } from "./utils/reportTransform";

import ReportIndividualUsageCard from "./ReportIndividualUsageCard";
import ReportOverallUsageCard from "./ReportOverallUsageCard";
import ReportSummary from "./ReportSummary";


type ReportOverviewTabProps = {
    summaryInfo: ReportSummaryInfo;
    timeRangeType: ReportTimeRangeType;
    timeRange: MonthTimeRange;

    numberOfRooms: number;
    numberOfEnergy: number;
    numberOfWater: number;
    numberOfWaterLeakage: number;

    isRoomLoading: boolean;
    isEnergyLoading: boolean;
    isWaterLoading: boolean;
    isWaterLeakageLoading: boolean;

    overviewData: ReportOverviewData;

    dateFormat: string;
};

const OVERVIEW_SECTION_CONFIG = [
    {
        key: 'room' as const,
        sectionTitle: 'Room Occupancy',
        overallTitle: 'Overall Room Occupancy',
        individualTitle: 'Individual Room Occupancy',
        unit: '',
        dataName: '',
        usageName: 'room',
        colorHex: REPORT_SENSOR_REGISTRY.room.colorHex,
        textHighlightClassName: REPORT_SENSOR_REGISTRY.room.colorClassName,
        colorClassName: REPORT_SENSOR_REGISTRY.room.backgroundColorClassName,
        headers: ['Rooms', 'Room Occupancy', 'Busiest Day', 'Daily Average'],
    },
    {
        key: 'energy' as const,
        sectionTitle: 'Energy Consumption',
        overallTitle: 'Overall Energy Consumption',
        individualTitle: 'Individual Energy Meter Consumption',
        unit: REPORT_SENSOR_REGISTRY.energy.unit,
        dataName: '',
        usageName: 'energy',
        colorHex: REPORT_SENSOR_REGISTRY.energy.colorHex,
        textHighlightClassName: REPORT_SENSOR_REGISTRY.energy.colorClassName,
        colorClassName: REPORT_SENSOR_REGISTRY.energy.backgroundColorClassName,
        headers: ['Meter', 'Energy Consumption', 'Highest Consumed Day', 'Daily Average'],
    },
    {
        key: 'water' as const,
        sectionTitle: 'Water Consumption',
        overallTitle: 'Overall Water Consumption',
        individualTitle: 'Individual Water Flow Meter Consumption',
        unit: REPORT_SENSOR_REGISTRY.water.unit,
        dataName: '',
        usageName: 'water',
        colorHex: REPORT_SENSOR_REGISTRY.water.colorHex,
        textHighlightClassName: REPORT_SENSOR_REGISTRY.water.colorClassName,
        colorClassName: REPORT_SENSOR_REGISTRY.water.backgroundColorClassName,
        headers: ['Meters', 'Water Consumption', 'Highest Consumed Day', 'Daily Average'],
    },
    {
        key: 'waterLeakage' as const,
        sectionTitle: 'Water Leakage Detection',
        overallTitle: 'Overall Water Leakage Detection',
        individualTitle: 'Individual Leakage Detection Sensor',
        unit: '',
        dataName: '',
        usageName: 'leakage detection',
        colorHex: REPORT_SENSOR_REGISTRY.waterLeakage.colorHex,
        textHighlightClassName: REPORT_SENSOR_REGISTRY.waterLeakage.colorClassName,
        colorClassName: REPORT_SENSOR_REGISTRY.waterLeakage.backgroundColorClassName,
        headers: ['Sensors', 'Detection Count', 'Highest Leakage Detected Day', 'Daily Average'],
    },
];

export default memo(function ReportOverviewTab({
    summaryInfo,
    timeRangeType,
    timeRange,

    numberOfRooms,
    numberOfEnergy,
    numberOfWater,
    numberOfWaterLeakage,

    isRoomLoading,
    isEnergyLoading,
    isWaterLoading,
    isWaterLeakageLoading,

    overviewData,

    dateFormat,
}: Readonly<ReportOverviewTabProps>) {
    const numberOfItemsByType = {
        room: numberOfRooms,
        energy: numberOfEnergy,
        water: numberOfWater,
        waterLeakage: numberOfWaterLeakage,
    };

    const isLoadingByType = {
        room: isRoomLoading,
        energy: isEnergyLoading,
        water: isWaterLoading,
        waterLeakage: isWaterLeakageLoading,
    };

    return (
        <>
            <ReportSummary
                summaryInfo={summaryInfo}
                timeRangeType={timeRangeType}

                isRoomLoading={isRoomLoading}
                isEnergyLoading={isEnergyLoading}
                isWaterLoading={isWaterLoading}
                isWaterLeakageLoading={isWaterLeakageLoading}
            />

            {OVERVIEW_SECTION_CONFIG.map((section) => (
                <div key={`overview-section-${section.key}`} className="d-block w-100">
                    <p className="report-main-section-title">{section.sectionTitle}</p>

                    <section className="d-block w-100 report-main-section">
                        <section className="d-flex w-100 m-0 p-0">
                            <ReportOverallUsageCard
                                title={section.overallTitle}
                                timeRangeType={timeRangeType}
                                itemType={section.key}
                                isLoading={isLoadingByType[section.key]}
                                textHighlightClassName={section.textHighlightClassName}
                                numberOfItems={numberOfItemsByType[section.key]}
                                unit={section.unit}
                                dataName={section.dataName}
                                dateFormat={dateFormat}
                                colorHex={section.colorHex}
                                selectedMonth={timeRange.endDate}
                                {...transformDataToOUCParams(section.key, timeRangeType, summaryInfo, overviewData[section.key].overallData)}
                            />
                            <ReportIndividualUsageCard
                                title={section.individualTitle}
                                itemType={section.key}
                                isLoading={isLoadingByType[section.key]}
                                timeRangeType={timeRangeType}
                                headers={section.headers}
                                data={overviewData[section.key].individualData}
                                usageName={section.usageName}
                                unit={section.unit}
                                colorClassName={section.colorClassName}
                                selectedMonth={timeRange.endDate}
                                dateFormat={dateFormat}
                            />
                        </section>
                    </section>
                </div>
            ))}

            <p className="report-tab-section-asterisk">*The value is rounded for display. Actual precision is higher.</p>
        </>
    );
});
